import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { UserDto } from '@bill-pulse/shared';
import { api, type RegisterPayload } from '@/lib/api';
import { usePreferencesStore } from '@/stores/preferences';
import { isNativeApp, loadSessionToken } from '@/lib/native';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserDto | null>(null);
  /** `null` tant qu'on n'a pas interrogé /auth/me au premier chargement. */
  const resolved = ref(false);

  async function bootstrap(): Promise<void> {
    if (resolved.value) return;
    try {
      // App sans jeton dans le trousseau : inutile d'interroger l'API.
      if (isNativeApp && !(await loadSessionToken())) {
        user.value = null;
        return;
      }
      user.value = await api.me();
    } catch {
      user.value = null;
    } finally {
      resolved.value = true;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    user.value = await api.login({ email, password });
    resolved.value = true;
    // App : nouvelle session, donc nouvel abonnement aux notifications.
    void import('@/lib/native-push').then((m) => m.resumePushAfterLogin());
  }

  async function register(data: RegisterPayload): Promise<void> {
    user.value = await api.register(data);
    resolved.value = true;
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      // L'état local est vidé quoi qu'il arrive : rester « connecté » dans
      // l'interface après un clic sur Déconnexion serait pire que tout.
      user.value = null;
      // Les préférences appartiennent au compte qui vient de se déconnecter :
      // la prochaine session (peut-être un autre compte) repart de zéro.
      usePreferencesStore().reset();
    }
  }

  /** Après suppression du compte : même nettoyage qu'une déconnexion, sans appel. */
  function forget(): void {
    user.value = null;
    usePreferencesStore().reset();
  }

  const isAdmin = computed(() => user.value?.role === 'admin');

  return { user, isAdmin, resolved, bootstrap, login, register, logout, forget };
});
